"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { ArrowRight, Menu, X, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerTrigger, DrawerTitle, DrawerHeader, DrawerClose } from "@/components/ui/drawer";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.label.toLowerCase() === "about" ? "/About" : item.label.toLowerCase() === "services" ? "/our-services/" : item.href || "#"} className="relative text-[#0B4D9C] text-sm font-sans font-medium tracking-wide hover:bg-transparent focus:bg-transparent px-0 py-2 group" style={{
            letterSpacing: "0.02em"
          }}>
                <span>{item.label}</span>
                <span className="absolute bottom-0 left-0 h-px w-0 bg-secondary transition-all duration-[250ms] ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-44 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="relative flex flex-col gap-8 px-8 py-4">
      <span className="absolute left-8 top-4 bottom-4 w-px bg-secondary" aria-hidden="true" />
      <div className="flex flex-col gap-8 pl-6">
        {menuItems.map((item, i) => <Link data-index={i} key={item.id} to={item.label.toLowerCase() === "about" ? "/About" : item.label.toLowerCase() === "services" ? "/our-services/" : item.href || "#"} onClick={onClose} className="font-default text-3xl text-foreground hover:text-secondary transition-colors duration-200">
            {item.label}
          </Link>)}
      </div>
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://hotelrevbaba.com';
    const hreflangs = [{
      rel: "alternate",
      hreflang: "en-in",
      href: `${origin}/?lang=en-in`
    }, {
      rel: "alternate",
      hreflang: "en-gb",
      href: `${origin}/?lang=en-gb`
    }, {
      rel: "alternate",
      hreflang: "en-th",
      href: `${origin}/?lang=en-th`
    }, {
      rel: "alternate",
      hreflang: "en-ke",
      href: `${origin}/?lang=en-ke`
    }, {
      rel: "alternate",
      hreflang: "en-tz",
      href: `${origin}/?lang=en-tz`
    }, {
      rel: "alternate",
      hreflang: "en-mv",
      href: `${origin}/?lang=en-mv`
    }, {
      rel: "alternate",
      hreflang: "en-ph",
      href: `${origin}/?lang=en-ph`
    }, {
      rel: "alternate",
      hreflang: "x-default",
      href: `${origin}/`
    }];
    const schemas = [{
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${origin}/`
      }, {
        "@type": "ListItem",
        "position": 2,
        "name": "Hotel Revenue Management Consultancy",
        "item": `${origin}/`
      }]
    }, {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "HotelRevBaBa",
      "description": "Expert hotel revenue management consultancy specializing in OTA optimization and hospitality tech stacks.",
      "url": `${origin}/`,
      "logo": `${origin}/logo.png`,
      "founder": {
        "@type": "Person",
        "name": "Sunil Singh"
      },
      "sameAs": ["https://www.linkedin.com/company/hotel-revbaba", "https://twitter.com/sunil_revbaba", "https://www.facebook.com/hotelrevbaba"],
      "areaServed": ["India", "UK", "Thailand", "Kenya", "Tanzania", "Maldives", "Philippines"],
      "contactPoint": [{
        "@type": "ContactPoint",
        "telephone": "+91-XXXXXXXXXX",
        "contactType": "customer service",
        "availableLanguage": ["English", "Hindi"]
      }]
    }, {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Hotel Revenue Management Consulting",
      "provider": {
        "@type": "Organization",
        "name": "HotelRevBaBa"
      },
      "description": "Comprehensive hospitality consulting including revenue optimization, OTA distribution auditing, and predictive ADR/RevPAR forecasting.",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Revenue Services",
        "itemListElement": [{
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Revenue Optimization"
          }
        }, {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "OTA Distribution Auditing"
          }
        }, {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "ADR & RevPAR Forecasting"
          }
        }]
      }
    }, {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [{
        "@type": "Question",
        "name": "What is an OTA distribution audit?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "An OTA distribution audit is a comprehensive review of your hotel's presence across online travel agencies to ensure rate parity, content accuracy, and ranking optimization."
        }
      }, {
        "@type": "Question",
        "name": "How does revenue management improve hotel profitability?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Revenue management uses data analytics and forecasting to sell the right room to the right guest at the right time, maximizing ADR and overall RevPAR."
        }
      }, {
        "@type": "Question",
        "name": "Does HotelRevBaBa provide tech stack recommendations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we specialize in evaluating and implementing hospitality technology including PMS, Channel Managers, and Revenue Management Systems (RMS)."
        }
      }]
    }, {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Sunil Singh",
      "jobTitle": "Freelance Hotel Revenue Manager",
      "description": "Sunil Singh is a seasoned hospitality professional with deep expertise in revenue management, OTA strategies, and hospitality technology implementation.",
      "sameAs": ["https://www.linkedin.com/company/hotel-revbaba", "https://twitter.com/sunil_revbaba"]
    }];
    const elements: (HTMLLinkElement | HTMLScriptElement)[] = [];
    hreflangs.forEach(lang => {
      const link = document.createElement("link");
      link.rel = lang.rel;
      link.hreflang = lang.hreflang;
      link.href = lang.href;
      document.head.appendChild(link);
      elements.push(link);
    });
    schemas.forEach(schema => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      elements.push(script);
    });
    return () => {
      elements.forEach(el => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, []);
  const dismissBanner = () => {
    setShowBanner(false);
    setTimeout(() => {
      setShowBanner(true);
    }, 30 * 60 * 1000);
  };
  return <>
      {showBanner && <div className="sticky top-0 z-[60] w-full bg-secondary text-white py-2 px-4 text-center flex items-center justify-center gap-4 shadow-md">
          <span className="text-sm font-medium">Claim your Free Revenue Audit today!</span>
          <Button asChild size="sm" variant="outline" className="bg-white text-secondary border-white hover:bg-white/90 h-7 px-3 text-xs">
            <Link to="#">Get Audit Now</Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={dismissBanner} className="absolute right-4 text-white hover:bg-white/20 size-8" aria-label="Dismiss banner">
            <X className="size-4" />
          </Button>
        </div>}

      <section data-section-id="749" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-out ${scrolled ? "bg-[#0B4D9C] text-white border-b border-white/10" : "bg-white text-[#0B4D9C] border-b border-border/50"}`}>
        <div className="mx-auto flex items-center justify-between px-6 sm:px-8 lg:px-12 transition-all duration-300 ease-out h-20">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="HotelRevBaBa home" className="flex items-center group">
              <span className="relative inline-flex items-center">
                <WvcLogo className="h-7 w-auto" />
                <span className="ml-1 inline-block h-1.5 w-1.5 rounded-lg bg-secondary transition-transform duration-300 group-hover:scale-125" />
              </span>
            </Link>
            <Link to="https://sunilsingh.co.in/" newTab className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors">
              Sunil Singh
            </Link>
          </div>

          <div className="hidden lg:flex items-center justify-center flex-1">
            <MenuProvider menu_id="42" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden lg:flex items-center">
            <Button asChild className="rounded-lg bg-[#0B4D9C] text-white hover:bg-[#0B4D9C]/90 px-6 py-2 h-auto text-sm font-sans font-medium tracking-wide group shadow-md">
              <Link to="/ContactPage">
                <span style={{
                letterSpacing: "0.02em"
              }}>Book Consultation</span>
                <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

          <div className="flex lg:hidden items-center">
            <SafeHydrate><Drawer open={mobileOpen} onOpenChange={setMobileOpen} direction="right">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-transparent">
                  <Menu className="h-[18px] w-[18px]" strokeWidth={1.5} />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="bg-background w-full sm:max-w-sm border-l border-secondary/20">
                <DrawerHeader className="flex items-center justify-between border-b border-border/50 px-6 py-5">
                  <DrawerTitle asChild>
                    <Link to="/" onClick={() => setMobileOpen(false)} aria-label="HotelRevBaBa home" className="inline-flex items-center">
                      <WvcLogo className="h-7 w-auto" />
                      <span className="ml-1 inline-block h-1.5 w-1.5 rounded-lg bg-secondary" />
                    </Link>
                  </DrawerTitle>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Close menu" className="text-foreground hover:bg-transparent">
                      <X className="h-[18px] w-[18px]" strokeWidth={1.5} />
                    </Button>
                  </DrawerClose>
                </DrawerHeader>

                <div className="flex-1 overflow-y-auto py-8">
                  <MenuProvider menu_id="42" className="header-menu-mobile">
                    <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="border-t border-border/50 px-6 py-6">
                  <Button asChild className="w-full rounded-lg bg-[#0B4D9C] text-white hover:bg-[#0B4D9C]/90 h-11 text-sm font-sans font-medium tracking-wide group shadow-md">
                    <Link to="/ContactPage" onClick={() => setMobileOpen(false)}>
                      <span style={{
                        letterSpacing: "0.02em"
                      }}>Book Consultation</span>
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </section>

      <Button asChild className="fixed bottom-6 right-6 z-50 size-14 rounded-lg bg-[#25D366] text-white shadow-2xl hover:bg-[#25D366]/90 transition-all hover:scale-110 p-0">
        <Link to="https://wa.me/91XXXXXXXXXX?text=Hi%20Sunil,%20I'd%20like%20a%20free%20revenue%20audit" newTab aria-label="Chat on WhatsApp">
          <MessageCircle className="size-7" />
        </Link>
      </Button>

      <Button asChild className="fixed bottom-24 right-6 z-50 hidden md:flex bg-[#0B4D9C] text-white px-4 py-2 rounded-lg shadow-lg hover:bg-[#0B4D9C]/90 transition-colors">
        <Link to="/ContactPage">
          Schedule Free Audit
        </Link>
      </Button>
    </>;
}